'use client';
import React, { memo, useEffect } from 'react';

import { IScrollspyTabsProps } from './ui-scrollspy-tabs.model';
import { Tabs, TabsList, TabsTrigger } from '@core/design-system/tailwind/ui/tabs';
import { cn } from '@core/design-system/tailwind/lib/utils';

import SectionTabsContext from '@mais-retorno-front/context/section-tabs';
import { useAuth } from '@core/features-core/auth/client';
import { PremiumTooltip } from '@mais-retorno-front/components/premium/tooltip';
import { SHEET_TABS_ID_SECTIONS } from '../constant';
import { UTMContent } from '@mais-retorno-front/components/utm-button/enum';

const UiScrollspyTabsComponent = ({
  sections,
  backgroundColor = '#f3f3f3',
  offset = 0,
  top = 0,
  ...props
}: IScrollspyTabsProps) => {
  const { setTabSelected, tabSelected }: any = SectionTabsContext();
  const { userIsPremium } = useAuth();

  useEffect(() => {
    return () => {
      setTabSelected('');
    };
  }, []);

  const handleScroll = () => {
    const sectionElements = sections.map((section) =>
      document.getElementById(section.id)
    );

    sectionElements.forEach((section) => {
      if (section) {
        const rect = section.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.top <= window.innerHeight / 2;

        if (isVisible) {
          setTabSelected(section.id);
        }
      }
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const goToSection = (sectionId: string) => {
    if (sectionId === sections?.[3]?.id) {
      window.scrollTo({
        top: 400,
        behavior: 'smooth',
      });
    } else {
      const element = document.querySelector(`#${sectionId}`);

      if (!element) return;
      // Aplica o offset para que o elemento não fique colado no topo
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleChange = (selectedTab: string) => {
    goToSection(selectedTab);
  };

  const handleClick = (id: string) => {
    setTabSelected(id);
  };

  return (
    <div
      id="tabs"
      className={cn(
        'my-2 sticky z-[900] border-b border-border shadow-sm',
        props?.className
      )}
      style={{
        backgroundColor,
        top,
        ...props?.style,
      }}
    >
      <Tabs
        value={tabSelected ? tabSelected : sections[0].id}
        onValueChange={handleChange}
        className="w-full"
      >
        <TabsList className="h-fit w-full justify-start overflow-x-auto bg-transparent border-b border-border rounded-none">
          {sections.map(({ id, name }) =>
            id === SHEET_TABS_ID_SECTIONS.MOVABLE_WINDOWS && !userIsPremium ? (
              <PremiumTooltip
                key={id}
                description="Para visualizar o Gráfico de Retorno em Janelas Móveis e obter outras vantagens, torne-se Prime."
                utmContent={UTMContent.CTA_WINDOWS}
              >
                <TabsTrigger value={id} className="min-w-[200px] min-h-[42px] text-base normal-case data-[state=active]:font-bold">
                  {name}
                </TabsTrigger>
              </PremiumTooltip>
            ) : (
              <TabsTrigger
                key={id}
                value={id}
                title={`Ir para a seção ${name}`}
                className="min-w-[200px] min-h-[42px] text-base normal-case data-[state=active]:font-bold"
                onClick={() => {
                  handleClick(id);
                }}
              >
                {name}
              </TabsTrigger>
            )
          )}
        </TabsList>
      </Tabs>
    </div>
  );
};

export const UiScrollspyTabs = memo(UiScrollspyTabsComponent);
